import { createContext, useContext, useState, useCallback, useEffect } from 'react';

const CartContext = createContext(null);

/** localStorage key for cart saved when guest tries to checkout; restored after sign-in */
export const CART_PENDING_CHECKOUT_KEY = 'flea_cart_pending_checkout';

export function CartProvider({ children }) {
  const [items, setItems] = useState([]);

  // Restore cart from localStorage when user returns after sign-in (e.g. redirect flow)
  useEffect(() => {
    try {
      const raw = localStorage.getItem(CART_PENDING_CHECKOUT_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) {
        setItems(parsed);
        localStorage.removeItem(CART_PENDING_CHECKOUT_KEY);
      }
    } catch {
      localStorage.removeItem(CART_PENDING_CHECKOUT_KEY);
    }
  }, []);

  const saveCartForCheckout = useCallback((currentItems) => {
    try {
      if (currentItems && currentItems.length > 0) {
        localStorage.setItem(CART_PENDING_CHECKOUT_KEY, JSON.stringify(currentItems));
      }
    } catch {
      // ignore
    }
  }, []);

  const restoreFromPendingCheckout = useCallback(() => {
    try {
      const raw = localStorage.getItem(CART_PENDING_CHECKOUT_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) {
        setItems(parsed);
        localStorage.removeItem(CART_PENDING_CHECKOUT_KEY);
      }
    } catch {
      localStorage.removeItem(CART_PENDING_CHECKOUT_KEY);
    }
  }, []);

  const removeItem = useCallback((id) => {
    setItems((prevItems) => prevItems.filter((item) => item.id !== id));
  }, []);

  const addItem = useCallback((id, quantity = 1) => {
    setItems((prevItems) => {
      const existingItem = prevItems.find((item) => item.id === id);
      if (existingItem) {
        return prevItems.map((item) =>
          item.id === id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }
      return [...prevItems, { id, quantity }];
    });
  }, []);

  const updateQuantity = useCallback((id, quantity) => {
    if (quantity <= 0) {
      removeItem(id);
      return;
    }
    setItems((prevItems) =>
      prevItems.map((item) =>
        item.id === id ? { ...item, quantity } : item
      )
    );
  }, [removeItem]);

  const getTotal = useCallback(() => {
    return items.reduce((total, item) => total + item.quantity, 0);
  }, [items]);

  const getItemQuantity = useCallback((id) => {
    const item = items.find((item) => item.id === id);
    return item ? item.quantity : 0;
  }, [items]);

  const clearCart = useCallback(() => {
    setItems([]);
  }, []);

  const removeItemsByIds = useCallback((ids) => {
    if (!ids || ids.length === 0) return;
    const idSet = new Set(ids.map(Number));
    setItems((prev) => prev.filter((item) => !idSet.has(Number(item.id))));
  }, []);

  const value = {
    items,
    addItem,
    updateQuantity,
    removeItem,
    removeItemsByIds,
    getTotal,
    getItemQuantity,
    clearCart,
    saveCartForCheckout,
    restoreFromPendingCheckout,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}
